import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Heading,
  Text,
  VStack,
  Alert,
  AlertIcon,
  useColorMode,
  IconButton,
  InputGroup,
  InputRightElement
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, SunIcon, MoonIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth, login } from '../hooks/useAuth';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { colorMode, toggleColorMode } = useColorMode();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const validate = () => {
    if (!email) return 'O e-mail é obrigatório.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return 'E-mail inválido.';
    if (!password) return 'A senha é obrigatória.';
    if (password.length < 6) return 'A senha deve ter pelo menos 6 caracteres.';
    return null;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao entrar');
    }
    setLoading(false);
  };

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}>
      <Box position="absolute" top={4} right={4}>
        <IconButton
          aria-label="Alternar modo de cor"
          icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
          onClick={toggleColorMode}
          variant="ghost"
        />
      </Box>
      <Box w="100%" maxW="400px" p={8} borderRadius="lg" boxShadow="lg" bg={colorMode === 'light' ? 'white' : 'gray.800'}>
        <VStack spacing={6} as="form" onSubmit={handleLogin}>
          <Heading size="lg" textAlign="center">Finance Control</Heading>
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}
          {success && (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              Login realizado com sucesso!
            </Alert>
          )}
          <FormControl isRequired>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Digite seu email"
              autoComplete="email"
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Senha</FormLabel>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Digite sua senha"
                autoComplete="current-password"
              />
              <InputRightElement h={'full'}>
                <IconButton
                  variant="ghost"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowPassword((show) => !show)}
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>
          <Button
            type="submit"
            colorScheme="teal"
            width="100%"
            isLoading={loading}
            isDisabled={loading}
          >
            Entrar
          </Button>
          <Text fontSize="sm" color="gray.500" textAlign="center">
            Não tem uma conta? <span style={{ color: '#319795', cursor: 'pointer' }} onClick={() => navigate('/register')}>Criar conta</span>
          </Text>
        </VStack>
      </Box>
    </Box>
  );
}; 